import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import {
  Clock,
  FileText,
  Plus,
  Edit,
  Trash2,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  ArrowLeft,
  AlertCircle,
  User,
  Search,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

function AuditLog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const filterIdentifier = searchParams.get("identifier") || "";

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState(filterIdentifier);

  // Detail modal
  const [detailLog, setDetailLog] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const PAGE_SIZE = 20;

  useEffect(() => {
    fetchLogs();
  }, [page, filterIdentifier]);

  const fetchLogs = async () => {
    setLoading(true);
    setError("");
    try {
      const skip = page * PAGE_SIZE;
      let url;
      if (filterIdentifier) {
        url = `/api/prompt-admin/audit/${encodeURIComponent(filterIdentifier)}?limit=${PAGE_SIZE}&skip=${skip}`;
      } else {
        url = `/api/prompt-admin/audit?limit=${PAGE_SIZE}&skip=${skip}`;
      }
      const { data } = await axios.get(url);
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error("Failed to fetch audit logs:", err);
      setError("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetail = async (logId) => {
    setDetailLoading(true);
    setShowDetail(true);
    setDetailLog(null);
    try {
      const { data } = await axios.get(
        `/api/prompt-admin/audit-detail/${logId}`,
      );
      setDetailLog(data.log);
    } catch (err) {
      console.error("Failed to fetch audit detail:", err);
      setError("Failed to load audit detail");
      setShowDetail(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    if (searchTerm.trim()) {
      setSearchParams({ identifier: searchTerm.trim() });
    } else {
      setSearchParams({});
    }
  };

  const clearFilter = () => {
    setSearchTerm("");
    setSearchParams({});
    setPage(0);
  };

  const getActionBadge = (action) => {
    switch (action) {
      case "create":
        return {
          icon: Plus,
          label: "Created",
          className: "bg-success-100 text-success-800",
        };
      case "update":
        return {
          icon: Edit,
          label: "Updated",
          className: "bg-primary-100 text-primary-800",
        };
      case "delete":
        return {
          icon: Trash2,
          label: "Deleted",
          className: "bg-danger-100 text-danger-800",
        };
      default:
        return {
          icon: Clock,
          label: action,
          className: "bg-gray-100 text-gray-800",
        };
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  if (!user) {
    navigate("/login");
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                <span className="text-lg font-semibold">Audit Log</span>
              </button>
            </div>
            {filterIdentifier && (
              <div className="flex items-center">
                <span className="text-sm text-gray-500 mr-2">
                  Filtered by:
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-100 text-primary-800">
                  {filterIdentifier}
                  <button
                    onClick={clearFilter}
                    className="ml-1.5 text-primary-600 hover:text-primary-900"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="p-4 bg-danger-50 border border-danger-200 rounded-md flex items-center">
            <AlertCircle className="h-5 w-5 text-danger-600 mr-2" />
            <span className="text-danger-800 text-sm">{error}</span>
            <button
              onClick={() => setError("")}
              className="ml-auto text-danger-600 hover:text-danger-800"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Search bar */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Filter by instruction identifier..."
                className="input-field pl-10 pr-24"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 space-x-1">
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearFilter}
                    className="text-gray-400 hover:text-gray-600 px-2 py-1"
                  >
                    Clear
                  </button>
                )}
                <button
                  type="submit"
                  className="bg-primary-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-primary-700"
                >
                  Filter
                </button>
              </div>
            </div>
          </form>

          {/* Summary */}
          <div className="mb-4 text-sm text-gray-600">
            {total} audit record{total !== 1 ? "s" : ""} found
            {filterIdentifier && (
              <span>
                {" "}
                for <strong>{filterIdentifier}</strong>
              </span>
            )}
          </div>

          {/* Audit Log Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading audit logs...</p>
            </div>
          ) : logs.length === 0 ? (
            <div className="card text-center py-12">
              <Clock className="h-12 w-12 text-gray-300 mx-auto" />
              <p className="mt-3 text-gray-500">No audit records found</p>
              {filterIdentifier && (
                <button
                  onClick={clearFilter}
                  className="mt-2 text-primary-600 hover:text-primary-800 text-sm font-medium"
                >
                  Clear filter
                </button>
              )}
            </div>
          ) : (
            <div className="card">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Action
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Instruction
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Performed By
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date & Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        View
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log) => {
                      const badge = getActionBadge(log.action);
                      const ActionIcon = badge.icon;
                      return (
                        <tr key={log._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}
                            >
                              <ActionIcon className="h-3 w-3 mr-1" />
                              {badge.label}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            <button
                              onClick={() => {
                                setSearchTerm(log.identifier);
                                setSearchParams({
                                  identifier: log.identifier,
                                });
                                setPage(0);
                              }}
                              className="hover:text-primary-600 hover:underline"
                            >
                              {log.identifier}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <User className="h-3.5 w-3.5 mr-1.5 text-gray-400" />
                              {log.performedBy || "unknown"}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {log.metadata?.fileSize != null && (
                              <span>
                                {(log.metadata.fileSize / 1024).toFixed(1)} KB
                                {log.metadata.lineCount != null && (
                                  <span className="text-gray-400">
                                    {" "}
                                    &middot; {log.metadata.lineCount} lines
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleViewDetail(log._id)}
                              className="text-primary-600 hover:text-primary-900"
                              title="View full detail"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                  <div className="text-sm text-gray-600">
                    Page {page + 1} of {totalPages} ({total} total)
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setPage((p) => Math.max(0, p - 1))}
                      disabled={page === 0}
                      className="btn-secondary flex items-center text-sm disabled:opacity-50"
                    >
                      <ChevronLeft className="h-4 w-4 mr-1" />
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setPage((p) => Math.min(totalPages - 1, p + 1))
                      }
                      disabled={page >= totalPages - 1}
                      className="btn-secondary flex items-center text-sm disabled:opacity-50"
                    >
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 z-50 bg-gray-900/60">
          <div className="h-full w-full p-4 sm:p-6 flex items-center justify-center">
            <div className="card w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Audit Detail
                  </h3>
                  {detailLog && (
                    <p className="text-sm text-gray-600 mt-1">
                      {detailLog.identifier} &mdash;{" "}
                      {getActionBadge(detailLog.action).label} by{" "}
                      {detailLog.performedBy || "unknown"}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setShowDetail(false);
                    setDetailLog(null);
                  }}
                  className="btn-secondary flex items-center"
                >
                  <X className="h-4 w-4 mr-1" />
                  Close
                </button>
              </div>

              {/* Modal Body */}
              <div className="overflow-auto flex-1 p-6">
                {detailLoading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading details...</p>
                  </div>
                ) : detailLog ? (
                  <div className="space-y-6">
                    {/* Metadata */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 uppercase">
                          Action
                        </p>
                        <span
                          className={`inline-flex items-center mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionBadge(detailLog.action).className}`}
                        >
                          {getActionBadge(detailLog.action).label}
                        </span>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 uppercase">
                          Performed By
                        </p>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {detailLog.performedBy || "unknown"}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 uppercase">
                          Date
                        </p>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {new Date(detailLog.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <p className="text-xs font-medium text-gray-500 uppercase">
                          Size / Lines
                        </p>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {detailLog.metadata?.fileSize != null
                            ? `${(detailLog.metadata.fileSize / 1024).toFixed(1)} KB`
                            : "-"}
                          {detailLog.metadata?.lineCount != null && (
                            <span className="text-gray-500">
                              {" "}
                              / {detailLog.metadata.lineCount} lines
                            </span>
                          )}
                        </p>
                      </div>
                    </div>

                    {/* Previous Content */}
                    {detailLog.previousContent != null && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                          <span className="inline-block w-3 h-3 rounded-full bg-red-400 mr-2"></span>
                          Previous Content
                        </h4>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-64 overflow-auto">
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                            {detailLog.previousContent}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* New Content */}
                    {detailLog.newContent != null && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center">
                          <span className="inline-block w-3 h-3 rounded-full bg-green-400 mr-2"></span>
                          New Content
                        </h4>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-64 overflow-auto">
                          <pre className="text-sm text-gray-800 whitespace-pre-wrap">
                            {detailLog.newContent}
                          </pre>
                        </div>
                      </div>
                    )}

                    {/* Deletion notice */}
                    {detailLog.action === "delete" &&
                      detailLog.newContent == null && (
                        <div className="bg-danger-50 border border-danger-200 rounded-lg p-4">
                          <p className="text-sm text-danger-800 font-medium">
                            This instruction was permanently deleted.
                          </p>
                        </div>
                      )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <AlertCircle className="h-8 w-8 text-gray-300 mx-auto" />
                    <p className="mt-2 text-gray-500">
                      Failed to load detail
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditLog;
